/* ============================================================================
   gestures.ts — Helpers gesture detection sur 21 landmarks MediaPipe Hands.

   Tous les helpers prennent en entree un tableau HandLandmark[] (x,y,z
   normalises 0..1, MediaPipe convention). 21 landmarks par main.

   Indices MediaPipe Hands :
     0  WRIST
     1-4   THUMB_CMC, MCP, IP, TIP
     5-8   INDEX_MCP, PIP, DIP, TIP
     9-12  MIDDLE_MCP, PIP, DIP, TIP
     13-16 RING_MCP, PIP, DIP, TIP
     17-20 PINKY_MCP, PIP, DIP, TIP
   ============================================================================ */

import type { HandLandmark } from '../canvasStore';

/** Centroide X (moyenne des x). Equivalent palm centroid. */
export function palmX(hand: HandLandmark[]): number {
  if (!hand.length) return 0.5;
  let s = 0;
  for (const lm of hand) s += lm.x;
  return s / hand.length;
}

/** Centroide Y (moyenne des y). */
export function palmY(hand: HandLandmark[]): number {
  if (!hand.length) return 0.5;
  let s = 0;
  for (const lm of hand) s += lm.y;
  return s / hand.length;
}

/** Centroide XY combine (moyenne des 21 landmarks). */
export function palmCentroid(hand: HandLandmark[]): { x: number; y: number } {
  if (!hand.length) return { x: 0.5, y: 0.5 };
  let sx = 0;
  let sy = 0;
  for (const lm of hand) {
    sx += lm.x;
    sy += lm.y;
  }
  return { x: sx / hand.length, y: sy / hand.length };
}

/**
 * Clap detector — true si les 2 paumes sont rapprochées (distance entre
 * leurs centroïdes < threshold). Issu du scan CX 2026-05-12 TOP 10 #3 :
 * pattern collidingScopes/stark-shapes (clap = trigger discret de transition).
 *
 * Threshold 0.18 = ~18% de la largeur normalisée frame. Suffisant pour
 * détecter un clap volontaire sans déclencher quand les 2 mains sont juste
 * proches naturellement (e.g. typing-like position).
 */
export function isClap(
  left: HandLandmark[] | null,
  right: HandLandmark[] | null,
  threshold = 0.18,
): boolean {
  if (!left || !right) return false;
  const cl = palmCentroid(left);
  const cr = palmCentroid(right);
  const dist = Math.hypot(cl.x - cr.x, cl.y - cr.y);
  return dist < threshold;
}

/** Largeur de paume (proxy : dist wrist=0 ↔ middle_mcp=9). */
function palmWidth(hand: HandLandmark[]): number {
  const w = hand[0];
  const m = hand[9];
  if (!w || !m) return 1;
  return Math.hypot(w.x - m.x, w.y - m.y) || 1;
}

/**
 * Pinch normalise 0..1.
 * 0 = pouce et index colles (serres)
 * 1 = pouce et index tres ecartes
 *
 * Calcul : dist(thumb_tip=4, index_tip=8) / palm_width, clampe.
 * Le ratio brut va ~0..2 ; on divise par 2 pour normaliser dans [0,1].
 */
export function computePinch(hand: HandLandmark[]): number {
  if (hand.length < 21) return 1;
  const t = hand[4];
  const i = hand[8];
  if (!t || !i) return 1;
  const dist = Math.hypot(t.x - i.x, t.y - i.y);
  const palmW = palmWidth(hand);
  const ratio = dist / palmW; // ~0..2
  return Math.max(0, Math.min(1, ratio / 2));
}

/**
 * Detecte main pointant index seul : index etendu, middle plie.
 * Heuristique en 2D (Y) : tip Y < pip Y => doigt etendu (pointe vers le haut
 * dans le repere image, donc Y plus petit). Pour le pouce on ignore (axe diff).
 *
 * Index etendu : tip[8].y < pip[6].y - 0.02 (assez clair)
 * Middle plie  : tip[12].y > mcp[9].y - 0.01 (tip pas au-dessus du mcp)
 *
 * On ignore ring/pinky pour rester tolerant (gun-finger / pointing).
 */
export function isIndexPointing(hand: HandLandmark[]): boolean {
  if (hand.length < 21) return false;
  const idxTip = hand[8];
  const idxPip = hand[6];
  const midTip = hand[12];
  const midMcp = hand[9];
  if (!idxTip || !idxPip || !midTip || !midMcp) {
    return false;
  }
  const indexExtended = idxTip.y < idxPip.y - 0.02;
  const middleCurled = midTip.y > midMcp.y - 0.01;
  return indexExtended && middleCurled;
}

/**
 * Detecte poing ferme : tous les 5 doigts (thumb/index/middle/ring/pinky)
 * plies. Pour chaque doigt : tip.y >= pip/mcp.y (tip pas au-dessus du
 * joint plus bas dans la chaine => plie). Tolerance +0.02 pour rester
 * indulgent quand la main bouge vite.
 *
 * Pouce : on compare tip(4) a mcp(2) — son axe principal est lateral mais
 * en poing ferme la y est generalement plus basse que mcp.
 */
export function isFist(hand: HandLandmark[]): boolean {
  if (hand.length < 21) return false;
  const wrist = hand[0];
  if (!wrist) return false;

  // PRIORITE : si l'index est etendu (pointing), c'est PAS un poing.
  // Le geste "index pointe" a 3 autres doigts plies (middle/ring/pinky)
  // ce qui matchait l'ancienne heuristique 3/4 et faussait fist=true.
  if (isIndexPointing(hand)) return false;

  // Heuristique : un doigt plie a son TIP plus proche du WRIST que ne l'est
  // son MCP. Main ouverte : tip BEAUCOUP plus loin que mcp.
  // Poing ferme : tip replie vers la paume, donc proche/sous le mcp.
  // On ignore le pouce (mecanique laterale, ratio ambigu).
  const fingers: Array<[number, number]> = [
    [8, 5],    // index : tip vs mcp
    [12, 9],   // middle
    [16, 13],  // ring
    [20, 17],  // pinky
  ];
  let curled = 0;
  for (const [tipIdx, mcpIdx] of fingers) {
    const tip = hand[tipIdx];
    const mcp = hand[mcpIdx];
    if (!tip || !mcp) continue;
    const dTipWrist = Math.hypot(tip.x - wrist.x, tip.y - wrist.y);
    const dMcpWrist = Math.hypot(mcp.x - wrist.x, mcp.y - wrist.y);
    if (dTipWrist < dMcpWrist) curled++;
  }
  // STRICT 4/4 : tous les doigts doivent etre plies.
  // Empeche les faux positifs sur peace sign (2/4) et index pointing (3/4).
  return curled >= 4;
}

/**
 * Detecte le "Y sign" / "V sign" / peace sign : index ET majeur etendus
 * vers le haut, autres doigts (ring + pinky) plies.
 *
 * Heuristique :
 *   - index (8) plus haut que index_pip (6)  → index etendu
 *   - middle (12) plus haut que middle_pip (10) → middle etendu
 *   - ring (16) plus bas que ring_pip (14) OU proche → ring plie
 *   - pinky (20) plus bas que pinky_pip (18) OU proche → pinky plie
 *   - thumb optionnellement plie (souvent cache en V sign)
 */
export function isPeaceSign(hand: HandLandmark[]): boolean {
  if (hand.length < 21) return false;
  const tol = 0.02;
  const indexExtended = !!hand[8] && !!hand[6] && hand[8].y < hand[6].y - tol;
  const middleExtended = !!hand[12] && !!hand[10] && hand[12].y < hand[10].y - tol;
  if (!indexExtended || !middleExtended) return false;
  // Ring + pinky doivent etre plies
  const ringCurled = !!hand[16] && !!hand[14] && hand[16].y > hand[14].y - tol;
  const pinkyCurled = !!hand[20] && !!hand[18] && hand[20].y > hand[18].y - tol;
  return Boolean(ringCurled && pinkyCurled);
}

/**
 * Detecte le "finger heart" K-pop (mini-coeur 1 main) :
 * - Pouce et index croises ou tres proches (forme un petit coeur)
 * - Au moins 1 des autres doigts (middle/ring/pinky) plie
 *
 * Heuristique relachee (vs version stricte initiale) car MediaPipe perd en
 * precision quand les doigts se chevauchent (occlusion). On accepte :
 *   - dist(thumb_tip=4, index_tip=8) < 0.35 * palm_width (assez proches)
 *   - au moins 1 doigt sur middle/ring/pinky plie (vs 2 avant)
 *
 * Note : ce geste se chevauche geometriquement avec le pinch normal. On
 * differencie en exigeant que >= 1 autre doigt soit plie (un pinch normal
 * a typiquement les 3 autres doigts ouverts).
 */
export function isFingerHeart(hand: HandLandmark[]): boolean {
  if (hand.length < 21) return false;
  const thumbTip = hand[4];
  const indexTip = hand[8];
  const wrist = hand[0];
  if (!thumbTip || !indexTip || !wrist) return false;

  // Pouce + index proches (seuil relache)
  const palmW = palmWidth(hand);
  const tipDist = Math.hypot(thumbTip.x - indexTip.x, thumbTip.y - indexTip.y);
  if (tipDist > palmW * 0.35) return false;

  // Middle/ring/pinky : meme heuristique que isFist (tip plus proche du
  // wrist que ne l'est le mcp). Au moins 1 doigt plie.
  const fingers: Array<[number, number]> = [
    [12, 9],   // middle : tip vs mcp
    [16, 13],  // ring
    [20, 17],  // pinky
  ];
  let curled = 0;
  for (const [tipIdx, mcpIdx] of fingers) {
    const tip = hand[tipIdx];
    const mcp = hand[mcpIdx];
    if (!tip || !mcp) continue;
    const dTipWrist = Math.hypot(tip.x - wrist.x, tip.y - wrist.y);
    const dMcpWrist = Math.hypot(mcp.x - wrist.x, mcp.y - wrist.y);
    if (dTipWrist < dMcpWrist) curled++;
  }
  return curled >= 1;
}

/**
 * Partitionne 2 mains detectees en left/right basees sur leur X.
 * Convention : la "right hand" de l'utilisateur apparait a GAUCHE de la frame
 * source (mirror webcam). Donc plus petit X = right hand.
 *
 * - 0 mains : { right: null, left: null }
 * - 1 main : on regarde sa position X pour determiner cote (avant on
 *   assignait toujours a right, ce qui causait pinch gauche → pinch droit
 *   quand l'user n'avait qu'une main gauche levee).
 * - 2 mains : on partitionne par X.
 */
export function partitionHands(
  landmarks: HandLandmark[][] | null | undefined,
): { right: HandLandmark[] | null; left: HandLandmark[] | null } {
  if (!landmarks || landmarks.length === 0) {
    return { right: null, left: null };
  }
  if (landmarks.length === 1) {
    const hand = landmarks[0]!;
    const x = palmX(hand);
    // X < 0.5 (cote gauche frame source = cote droit user en mirror selfie)
    // → main droite. Sinon → main gauche.
    if (x < 0.5) {
      return { right: hand, left: null };
    }
    return { right: null, left: hand };
  }
  const a = landmarks[0]!;
  const b = landmarks[1]!;
  const xa = palmX(a);
  const xb = palmX(b);
  // Plus petit X (cote gauche dans la source mirror) = main droite user.
  if (xa <= xb) {
    return { right: a, left: b };
  }
  return { right: b, left: a };
}
